import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  InvitationListResponse,
  AlbumInvitationWithUrl,
  CreateInvitationRequest,
  AlbumDetail,
} from '@omokage/shared';
import { collaboratorKeys } from './useCollaborators';
import { albumKeys } from './useAlbums';

/**
 * Query key factory for invitation queries
 */
export const invitationKeys = {
  all: ['invitations'] as const,
  lists: () => [...invitationKeys.all, 'list'] as const,
  list: (albumId: string) => [...invitationKeys.lists(), albumId] as const,
  info: (token: string) => [...invitationKeys.all, 'info', token] as const,
};

/**
 * Hook to fetch invitations for a specific album (owner only)
 *
 * @param albumId - The album ID to fetch invitations for
 * @returns Query result with array of invitations
 */
export function useInvitations(albumId: string) {
  return useQuery({
    queryKey: invitationKeys.list(albumId),
    queryFn: async () => {
      const response = await apiClient.get<InvitationListResponse>(
        `/albums/${albumId}/invitations`
      );
      return response.invitations;
    },
    enabled: !!albumId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new invitation link (owner only)
 *
 * @param albumId - The album ID to create invitation for
 * @returns Mutation function to create an invitation
 */
export function useCreateInvitation(albumId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvitationRequest) => {
      const response = await apiClient.post<AlbumInvitationWithUrl>(
        `/albums/${albumId}/invitations`,
        data
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate invitations list
      queryClient.invalidateQueries({
        queryKey: invitationKeys.list(albumId),
      });
    },
  });
}

/**
 * Hook to delete an invitation link (owner only)
 *
 * @returns Mutation function to delete an invitation
 */
export function useDeleteInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invitationId, albumId }: { invitationId: string; albumId: string }) => {
      await apiClient.delete<{ success: true; message: string }>(
        `/albums/${albumId}/invitations/${invitationId}`
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate invitations list
      queryClient.invalidateQueries({
        queryKey: invitationKeys.list(variables.albumId),
      });
    },
  });
}

/**
 * Hook to fetch invitation info by token (public, no auth required)
 *
 * @param token - The invitation token
 * @returns Query result with album information
 */
export function useInviteInfo(token: string) {
  return useQuery({
    queryKey: invitationKeys.info(token),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: true;
        data: {
          albumId: string;
          albumName: string;
          albumDescription: string | null;
          ownerDisplayName: string | null;
        };
      }>(`/albums/invitations/${token}/info`);
      return response.data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to accept an invitation and join an album
 *
 * @returns Mutation function to accept an invitation
 */
export function useAcceptInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const response = await apiClient.post<{ success: true; data: AlbumDetail }>(
        `/albums/invitations/${token}/accept`
      );
      return response.data;
    },
    onSuccess: (album) => {
      // Invalidate album lists to show the new album
      queryClient.invalidateQueries({
        queryKey: albumKeys.lists(),
      });
      // Add the album to cache
      queryClient.setQueryData(albumKeys.detail(album.id), album);
      // Invalidate collaborators to show current user
      queryClient.invalidateQueries({
        queryKey: collaboratorKeys.list(album.id),
      });
    },
  });
}

/**
 * Copy invitation URL to clipboard
 *
 * @param inviteUrl - The full invitation URL to copy
 * @returns Promise that resolves to true if successful
 */
export async function copyInviteUrl(inviteUrl: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(inviteUrl);
      return true;
    }

    // Fallback for older browsers or non-HTTPS contexts
    const textArea = document.createElement('textarea');
    textArea.value = inviteUrl;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch {
    return false;
  }
}
